/**
 * MUTAGIM.SHOP — Phase 16: Social Calendar
 * Calendrier de publication des posts sociaux
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon,
  Clock, Instagram, Facebook, Video, Loader2, Eye
} from 'lucide-react';
import { AdminPageWrapper } from '../components/admin/AdminPageWrapper';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

function getToken() {
  return localStorage.getItem('admin_token') || localStorage.getItem('auth_token');
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1; // Convert Sunday (0) to end of week
}

export function AdminSocialCalendarPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchCalendar();
  }, [year, month, navigate]);

  const fetchCalendar = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/social/calendar?year=${year}&month=${month + 1}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCalendarData(data.calendar || {});
      }
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'bg-green-500';
      case 'scheduled': return 'bg-blue-500';
      case 'error': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getPlatformIcon = (platforms) => {
    if (platforms?.includes('instagram')) return Instagram;
    if (platforms?.includes('facebook')) return Facebook;
    if (platforms?.includes('tiktok')) return Video;
    return CalendarIcon;
  };

  // Generate calendar grid
  const calendarGrid = [];
  let dayCounter = 1;
  
  for (let week = 0; week < 6; week++) {
    const weekDays = [];
    for (let day = 0; day < 7; day++) {
      if ((week === 0 && day < firstDay) || dayCounter > daysInMonth) {
        weekDays.push(null);
      } else {
        const dayPosts = calendarData[dayCounter] || [];
        weekDays.push({
          day: dayCounter,
          posts: dayPosts,
          isToday: new Date().getDate() === dayCounter && 
                   new Date().getMonth() === month && 
                   new Date().getFullYear() === year
        });
        dayCounter++;
      }
    }
    calendarGrid.push(weekDays);
    if (dayCounter > daysInMonth) break;
  }

  return (
    <AdminPageWrapper title="Calendrier de Publication" subtitle="Gérez vos posts planifiés">
      <div className="space-y-6" data-testid="social-calendar-page">
        <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <Button variant="ghost" onClick={prevMonth}>
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <CardTitle className="text-white text-xl">
                  {MONTHS[month]} {year}
                </CardTitle>
                <Button variant="ghost" onClick={nextMonth}>
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
                </div>
              ) : (
                <>
                  {/* Days of week header */}
                  <div className="grid grid-cols-7 gap-2 mb-4">
                    {DAYS_OF_WEEK.map((day) => (
                      <div key={day} className="text-center text-white text-sm font-medium py-2">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar grid */}
                  <div className="grid grid-cols-7 gap-2">
                    {calendarGrid.flat().map((cell, idx) => (
                      <div
                        key={idx}
                        onClick={() => cell && setSelectedDay(cell)}
                        className={cn(
                          "min-h-[100px] p-2 rounded-lg border transition-all",
                          cell 
                            ? cn(
                                "border-zinc-800 hover:border-zinc-600 cursor-pointer",
                                cell.isToday && "border-blue-500 bg-blue-500/10",
                                selectedDay?.day === cell.day && "border-purple-500 bg-purple-500/10"
                              )
                            : "border-transparent bg-zinc-900/30"
                        )}
                      >
                        {cell && (
                          <>
                            <div className={cn(
                              "text-sm font-medium mb-2",
                              cell.isToday ? "text-blue-400" : "text-white"
                            )}>
                              {cell.day}
                            </div>
                            
                            {/* Posts for this day */}
                            <div className="space-y-1">
                              {cell.posts.slice(0, 3).map((post, i) => {
                                const Icon = getPlatformIcon(post.platforms);
                                return (
                                  <div 
                                    key={i}
                                    className="flex items-center gap-1 p-1 rounded bg-zinc-800/50 text-xs"
                                  >
                                    <div className={cn("w-2 h-2 rounded-full", getStatusColor(post.status))} />
                                    <Icon className="w-3 h-3 text-white" />
                                    <span className="text-zinc-300 truncate flex-1">
                                      {post.product_name || 'Post'}
                                    </span>
                                  </div>
                                );
                              })}
                              {cell.posts.length > 3 && (
                                <div className="text-xs text-gray-200 text-center">
                                  +{cell.posts.length - 3} autres
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Selected Day Detail */}
          {selectedDay && selectedDay.posts.length > 0 && (
            <Card className="bg-zinc-900/50 border-zinc-800 mt-6">
              <CardHeader>
                <CardTitle className="text-white">
                  Posts du {selectedDay.day} {MONTHS[month]}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {selectedDay.posts.map((post, i) => (
                    <div 
                      key={i}
                      className="flex items-center gap-4 p-4 bg-zinc-800/50 rounded-lg"
                    >
                      {post.product_image && (
                        <img 
                          src={post.product_image} 
                          alt="" 
                          className="w-16 h-16 object-cover rounded"
                        />
                      )}
                      <div className="flex-1">
                        <h4 className="text-white font-medium">{post.product_name || 'Post'}</h4>
                        <p className="text-sm text-white line-clamp-1">{post.copy_hook}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className={getStatusColor(post.status)}>
                            {post.status}
                          </Badge>
                          <span className="text-xs text-gray-200 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(post.scheduled_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {post.platforms?.map((p) => {
                          const icons = { instagram: Instagram, facebook: Facebook, tiktok: Video };
                          const Icon = icons[p] || CalendarIcon;
                          return <Icon key={p} className="w-5 h-5 text-white" />;
                        })}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/social?post=${post.id}`)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Legend */}
          <div className="flex items-center gap-6 mt-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-white">Publié</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-white">Planifié</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-white">Erreur</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gray-500" />
              <span className="text-white">Brouillon</span>
            </div>
          </div>
      </div>
    </AdminPageWrapper>
  );
}

export default AdminSocialCalendarPage;
